import React, { useState, useEffect } from 'react';
import AdminHeader from '../components/AdminHeader';
import NavSidebar from "../components/NavSidebar";
import { SkeletonText } from "../components/shared/Skeleton";
import {
  getUpsellInteractions, 
  markUpsellAsRead, 
  markAllUpsellAsRead,
  getUpsellStatistics
} from '../api/auth';
import { useNotification } from '../contexts/NotificationContext';

const UpsellNotificationsPage = () => {
  const { showSuccess, showError } = useNotification();
  const [interactions, setInteractions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    action: '',
    product: '',
    is_read: '',
  });

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    total_pages: 0
  });

  useEffect(() => {
    fetchInteractions();
    fetchStatistics();
  }, [filters]);

  const fetchInteractions = async () => {
    setLoading(true);
    try {
      const response = await getUpsellInteractions(filters);
      setInteractions(response.data?.interactions || []);
      setPagination(response.data?.pagination || {});
    } catch (err) {
      console.error('Failed to fetch interactions:', err);
      showError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await getUpsellStatistics();
      setStatistics(response.data || null);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  const handleMarkAsRead = async (ids) => {
    try {
      await markUpsellAsRead(ids);
      showSuccess(`Marked ${ids.length} notification(s) as read`);
      setSelectedIds([]);
      fetchInteractions();
      // Refresh statistics
      fetchStatistics();
    } catch (err) {
      console.error('Failed to mark as read:', err);
      showError('Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllUpsellAsRead();
      showSuccess('Marked all notifications as read');
      fetchInteractions();
      fetchStatistics();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      showError('Failed to mark all as read');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === interactions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(interactions.map(i => i.id));
    }
  };

  const getActionBadge = (action) => {
    const badges = {
      viewed: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      remind_later: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      started_trial: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      dismissed: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
    };
    return badges[action] || badges.viewed;
  };

  const getActionIcon = (action) => {
    if (action === 'viewed') return '👁️';
    if (action === 'remind_later') return '⏰';
    if (action === 'started_trial') return '✅';
    if (action === 'dismissed') return '❌';
    return '📌';
  };

  const getProductBadge = (product) => {
    return product === 'voice' 
      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
      : 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400';
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#f8fbfe] dark:bg-gray-950 transition-colors duration-200">
      <AdminHeader activePage="Notifications" />

      <div className="flex">
        <NavSidebar activePage={null} />
        <main className="flex-1 min-w-0">
      <div className="flex-grow px-10 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Upsell Notifications
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Track customer interest in Voice & Chat products
            </p>
          </div>

          {/* Statistics Cards */}
          {statistics && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border dark:border-gray-800">
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Interactions</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {statistics.total || 0}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border dark:border-gray-800">
                <div className="text-sm text-gray-500 dark:text-gray-400">Trials Started</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {statistics.trials_started || 0}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border dark:border-gray-800">
                <div className="text-sm text-gray-500 dark:text-gray-400">Viewed</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                  {statistics.viewed || 0}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border dark:border-gray-800">
                <div className="text-sm text-gray-500 dark:text-gray-400">Conversion Rate</div>
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                  {statistics.conversion_rate || 0}%
                </div>
              </div>
            </div>
          )}

          {/* Filters & Actions */}
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border dark:border-gray-800 mb-6">
            <div className="flex flex-wrap gap-3 items-center">
              <select
                value={filters.action}
                onChange={(e) => setFilters({...filters, action: e.target.value, page: 1})}
                className="px-3 py-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
              >
                <option value="">All Actions</option>
                <option value="viewed">Viewed</option>
                <option value="remind_later">Remind Later</option>
                <option value="started_trial">Started Trial</option>
                <option value="dismissed">Dismissed</option>
              </select>

              <select
                value={filters.product}
                onChange={(e) => setFilters({...filters, product: e.target.value, page: 1})}
                className="px-3 py-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
              >
                <option value="">All Products</option>
                <option value="voice">Voice</option>
                <option value="chat">Chat</option>
              </select>

              <select
                value={filters.is_read}
                onChange={(e) => setFilters({...filters, is_read: e.target.value, page: 1})}
                className="px-3 py-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
              >
                <option value="">All Status</option>
                <option value="false">Unread</option>
                <option value="true">Read</option>
              </select>

              <div className="flex-grow"></div>

              {selectedIds.length > 0 && (
                <button
                  onClick={() => handleMarkAsRead(selectedIds)}
                  className="px-4 py-2 bg-[#32B1FF] text-white rounded-lg hover:bg-[#259ae6] text-sm font-medium"
                >
                  Mark Selected as Read ({selectedIds.length})
                </button>
              )}

              <button
                onClick={handleMarkAllAsRead}
                className="px-4 py-2 border dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-medium"
              >
                Mark All as Read
              </button>
            </div>
          </div>

          {/* Interactions Table */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 overflow-hidden">
            {loading ? (
              <div className="p-4">
                <SkeletonText lines={8} lineClass="h-10" gapClass="space-y-2" />
              </div>
            ) : interactions.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No notifications found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedIds.length === interactions.length && interactions.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Action
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Company
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-800">
                    {interactions.map((interaction) => (
                      <tr 
                        key={interaction.id} 
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                          interaction.is_read ? 'opacity-60' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(interaction.id)}
                            onChange={() => toggleSelect(interaction.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          {interaction.is_read ? (
                            <span className="text-gray-400 text-sm">Read</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-sm font-medium text-[#32B1FF]">
                              <span className="w-2 h-2 bg-[#32B1FF] rounded-full"></span>
                              New
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getActionBadge(interaction.action)}`}>
                            <span>{getActionIcon(interaction.action)}</span>
                            {interaction.action.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getProductBadge(interaction.product)}`}>
                            {interaction.product === 'voice' ? '📞' : '💬'} {interaction.product}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                            {interaction.user?.display_name || interaction.user?.email || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {interaction.user?.email}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {interaction.company?.name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(interaction.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-4 py-3 border-t dark:border-gray-800 flex flex-col gap-3 min-[412px]:flex-row min-[412px]:items-center min-[412px]:justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilters({...filters, page: Math.max(1, filters.page - 1)})}
                    disabled={pagination.page === 1}
                    className="px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setFilters({...filters, page: Math.min(pagination.total_pages, filters.page + 1)})}
                    disabled={pagination.page === pagination.total_pages}
                    className="px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
        </main>
      </div>
    </div>
  );
};

export default UpsellNotificationsPage;

